import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  X, Mail, Phone, MapPin, Globe, ExternalLink, Copy, CalendarPlus,
  LogIn, UserPlus, TicketCheck, Building2, CreditCard, Ticket,
} from "lucide-react";

const money = (p, country) => formatMoney(p, country === "US" ? "USD" : "GBP");
const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const TIMELINE_ICON = {
  account_registered: UserPlus,
  login: LogIn,
  venue_updated: Building2,
  stripe_connected: CreditCard,
  event_created: CalendarPlus,
  ticket_sold: TicketCheck,
};

export default function VenueDrawer({ userId, onClose, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("timeline");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/admin/venues/${userId}`);
        setDetail(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load venue detail");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const copy = (val) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success("Copied");
  };

  const summary = detail?.summary;
  const timeline = detail?.timeline || [];
  const events = detail?.events || [];
  const orders = detail?.orders || [];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="venue-drawer">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-zinc-900/40 backdrop-blur-sm"
        data-testid="venue-drawer-scrim"
      />
      <aside className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-zinc-200 z-10">
          <div className="p-6 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">/ account</div>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-11 h-11 shrink-0 rounded-full bg-zinc-900 text-white grid place-items-center font-display font-black">
                  {(summary?.venue_name || summary?.name || summary?.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-black text-2xl tracking-tight truncate">{summary?.venue_name || summary?.name || "—"}</div>
                  <div className="text-sm text-zinc-500 truncate">{summary?.email}</div>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="venue-drawer-close">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Tabs */}
          <div className="px-6 flex items-center gap-4 border-t border-zinc-100">
            {[
              ["timeline", `Timeline (${timeline.length})`],
              ["events",   `Events (${events.length})`],
              ["orders",   `Orders (${orders.length})`],
              ["info",     "Contact info"],
            ].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`venue-drawer-tab-${k}`}
                className={`py-3 text-sm font-medium transition ${tab === k ? "text-zinc-900 border-b-2 border-indigo-600 -mb-px" : "text-zinc-500 hover:text-zinc-900"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading ? (
            <div className="text-sm text-zinc-500 py-10 text-center">Loading detail…</div>
          ) : !detail ? (
            <div className="text-sm text-zinc-500 py-10 text-center">Nothing to show.</div>
          ) : (
            <>
              {/* Stats strip */}
              <div className="grid grid-cols-4 gap-3 mb-8">
                <StatBox label="Events" value={summary.events_count} />
                <StatBox label="Published" value={summary.published_events_count} />
                <StatBox label="Tickets sold" value={summary.tickets_sold} />
                <StatBox label="GMV" value={money(summary.revenue_pence, summary.country)} />
              </div>

              {tab === "timeline" && (
                <div className="space-y-4" data-testid="venue-drawer-timeline">
                  {timeline.length === 0 ? (
                    <div className="text-sm text-zinc-500 italic">No activity recorded yet.</div>
                  ) : (
                    timeline.map((ev, i) => {
                      const Icon = TIMELINE_ICON[ev.type] || Ticket;
                      return (
                        <div key={i} className="flex gap-4">
                          <div className="w-9 h-9 shrink-0 rounded-full bg-zinc-100 text-zinc-700 grid place-items-center">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1 pb-4 border-b border-zinc-100">
                            <div className="text-sm font-medium text-zinc-900">{ev.label}</div>
                            {ev.detail && <div className="text-xs text-zinc-500 mt-0.5 break-words">{ev.detail}</div>}
                            <div className="text-[11px] text-zinc-400 mt-1 font-mono">{fmtDT(ev.at)}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {tab === "events" && (
                <div className="space-y-3" data-testid="venue-drawer-events">
                  {events.length === 0 ? (
                    <div className="text-sm text-zinc-500 italic">No events created yet.</div>
                  ) : (
                    events.map((e) => (
                      <div key={e.id} className="p-3 rounded-lg border border-zinc-200 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{e.title}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {e.status} · created {fmtDT(e.created_at)}
                          </div>
                        </div>
                        <div className="text-xs text-zinc-400 font-mono">{e.start_at ? new Date(e.start_at).toLocaleDateString("en-GB") : "—"}</div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {tab === "orders" && (
                <div className="space-y-3" data-testid="venue-drawer-orders">
                  {orders.length === 0 ? (
                    <div className="text-sm text-zinc-500 italic">No orders yet.</div>
                  ) : (
                    orders.map((o) => (
                      <div key={o.id} className="p-3 rounded-lg border border-zinc-200 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">#{o.order_number} · {o.buyer_name || o.buyer_email}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {o.tickets} ticket(s) · {o.status} · {fmtDT(o.created_at)}
                          </div>
                        </div>
                        <div className="text-sm font-mono">{money(o.total_pence, summary.country)}</div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {tab === "info" && (
                <div className="space-y-3" data-testid="venue-drawer-info">
                  <InfoRow icon={Mail}     label="Email"         value={summary.email}         onCopy={() => copy(summary.email)} />
                  <InfoRow icon={Building2}label="Venue name"    value={summary.venue_name} />
                  <InfoRow icon={Mail}     label="Contact email" value={summary.contact_email} onCopy={() => copy(summary.contact_email)} />
                  <InfoRow icon={Phone}    label="Phone"         value={summary.contact_phone} onCopy={() => copy(summary.contact_phone)} />
                  <InfoRow icon={MapPin}   label="Address"       value={summary.address} />
                  <InfoRow icon={Globe}    label="Website"       value={summary.website}       link={summary.website} />
                  <InfoRow icon={CreditCard} label="Stripe"      value={summary.stripe_onboarded ? "Onboarded" : "Not connected"} />
                  <InfoRow icon={LogIn}    label="Last login"    value={fmtDT(summary.last_login_at)} />
                  <InfoRow icon={UserPlus} label="Registered"    value={fmtDT(summary.created_at)} />
                  {summary.venue_slug && (
                    <InfoRow
                      icon={ExternalLink}
                      label="Public venue"
                      value={`/v/${summary.venue_slug}`}
                      link={`/v/${summary.venue_slug}`}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 font-display font-black text-xl tracking-tight text-zinc-900">{value ?? 0}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, link, onCopy }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-zinc-100 last:border-none">
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-8 h-8 shrink-0 rounded-lg bg-zinc-100 grid place-items-center">
          <Icon className="w-4 h-4 text-zinc-500" />
        </span>
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
          {link ? (
            <a href={link} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline break-words">{value || "—"}</a>
          ) : (
            <div className="text-sm text-zinc-900 break-words">{value || "—"}</div>
          )}
        </div>
      </div>
      {onCopy && value && (
        <button type="button" onClick={onCopy} className="shrink-0 text-zinc-400 hover:text-zinc-900" aria-label="Copy">
          <Copy className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
